import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GH } from '../theme';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Reveal } from '../components/Reveal';
import { SectionLabel } from '../components/SectionLabel';
import { GHButton } from '../components/GHButton';
import { ImagePlaceholder } from '../components/ImagePlaceholder';

function HeroSection({ navigate }) {
  const [loaded, setLoaded] = useState(false);
  const w = useWindowWidth();
  const mob = w < 768;
  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);
  return (
    <section style={{ position: 'relative', height: '100vh', minHeight: mob ? 600 : 700, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      <img src="/images/hero-bg.png" alt="" aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 35%', zIndex: 0 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(28,31,35,0.82) 0%, rgba(28,31,35,0.65) 40%, rgba(28,31,35,0.85) 100%)', zIndex: 1 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 55% at 55% 40%, rgba(74,124,111,0.08) 0%, transparent 65%)', zIndex: 2 }} />
      <div style={{ maxWidth: 1300, margin: '0 auto', padding: mob ? '0 20px' : '0 40px', width: '100%', position: 'relative', zIndex: 3 }}>
        <div style={{ maxWidth: 780 }}>
          <h1 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: mob ? 'clamp(36px, 10vw, 52px)' : 'clamp(48px, 6.5vw, 88px)', color: '#fff', letterSpacing: '-0.035em', lineHeight: 1.0, margin: '0 0 24px', opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(24px)', transition: 'all 0.8s ease 0.25s' }}>
            <span style={{ color: GH.colors.gold }}>Construction</span><br />
            &amp; Property Services<br />
            <span style={{ fontSize: '0.55em', fontWeight: 600, letterSpacing: '-0.01em', color: GH.colors.onDark.body }}>by GreyHolm Group</span>
          </h1>
          <p style={{ fontFamily: GH.fonts.body, fontSize: mob ? 15 : 'clamp(16px, 1.8vw, 19px)', color: GH.colors.onDark.body, lineHeight: 1.65, margin: '0 0 36px', maxWidth: 560, opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(20px)', transition: 'all 0.8s ease 0.4s' }}>
            From residential construction to complete property maintenance, GreyHolm Group delivers reliable workmanship with professional results across Auckland.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(16px)', transition: 'all 0.8s ease 0.55s' }}>
            <GHButton size={mob ? 'md' : 'lg'} onClick={() => navigate('quote')}>Get a Quote</GHButton>
            <GHButton size={mob ? 'md' : 'lg'} variant="outline" onClick={() => navigate('services')}>Explore Services</GHButton>
          </div>
        </div>
      </div>
      {!mob && <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: 0.4 }}>
        <div style={{ width: 1, height: 48, background: '#fff', animation: 'scrollPulse 2s ease-in-out infinite' }} />
      </div>}
    </section>
  );
}

// ─── SERVICES OVERVIEW ───────────────────────────────────────────────────────

function ServicesOverviewSection({ navigate }) {
  const w = useWindowWidth();
  const mob = w < 768;
  const tab = w < 1024 && w >= 768;
  const cols = mob ? '1fr' : tab ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)';
  const services = [
    { num: '01', title: 'Residential Construction', desc: 'New builds, renovations, structural upgrades for Auckland homeowners.' },
    { num: '02', title: 'Property Maintenance', desc: 'Ongoing planned and reactive maintenance for commercial and residential properties.' },
    { num: '03', title: 'Window Cleaning', desc: 'Professional commercial and residential window cleaning services.' },
    { num: '04', title: 'Water Blasting', desc: 'High-pressure cleaning for driveways, facades, cladding, decks, and warehouses.' },
    { num: '05', title: 'Building Painting', desc: 'Interior and exterior painting with premium finishes for all property types.' },
    { num: '06', title: 'Fencing & Concrete', desc: 'New fence installation, concrete driveways, garage slabs, and retaining walls.' },
  ];
  return (
    <section style={{ background: GH.colors.bg, padding: mob ? '80px 20px' : '120px 40px' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        <Reveal style={{ marginBottom: mob ? 40 : 64 }}>
          <SectionLabel>What We Do</SectionLabel>
          <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(30px, 4vw, 52px)', color: GH.colors.white, letterSpacing: '-0.03em', lineHeight: 1.05, margin: '20px 0 0', maxWidth: 500 }}>
            Complete Property<br />Service Solutions
          </h2>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 1, background: GH.colors.borderDk }}>
          {services.map((s, i) => (
            <ServiceCard key={i} {...s} delay={i * 60} onClick={() => navigate('services')} />
          ))}
        </div>
        <Reveal delay={300} style={{ marginTop: 48, display: 'flex', justifyContent: 'center' }}>
          <GHButton variant="secondary" size="lg" onClick={() => navigate('services')}>View All Services</GHButton>
        </Reveal>
      </div>
    </section>
  );
}

function ServiceCard({ num, title, desc, delay, onClick }) {
  const [hov, setHov] = useState(false);
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? GH.colors.cardHover : GH.colors.card, padding: '40px 36px', cursor: 'pointer', transition: 'background 0.25s ease',
        borderTop: `2px solid ${hov ? GH.colors.gold : 'transparent'}`, opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(20px)',
        transitionProperty: 'opacity, transform, background, border-color', transitionDuration: '0.6s, 0.6s, 0.25s, 0.25s', transitionDelay: `${delay}ms` }}>
      <div style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: '0.12em', color: GH.colors.accent, marginBottom: 16 }}>{num}</div>
      <h3 style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 20, color: GH.colors.white, letterSpacing: '-0.015em', lineHeight: 1.2, margin: '0 0 14px' }}>{title}</h3>
      <p style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk, lineHeight: 1.7, margin: 0 }}>{desc}</p>
      <div style={{ marginTop: 28, display: 'flex', alignItems: 'center', gap: 8, fontFamily: GH.fonts.body, fontSize: 13, fontWeight: 600, color: hov ? GH.colors.gold : GH.colors.greyLight, transition: 'color 0.2s' }}>
        Learn more <span>→</span>
      </div>
    </div>
  );
}

// ─── WHY CHOOSE GREYHOLM ─────────────────────────────────────────────────────

function WhyChooseSection() {
  const w = useWindowWidth();
  const mob = w < 768;
  const items = [
    { title: 'Precision Workmanship', desc: 'Every project meets exacting standards. We never cut corners on quality or safety.' },
    { title: 'Local Auckland Expertise', desc: 'Deep knowledge of Auckland\'s building codes, climate conditions, and construction landscape.' },
    { title: 'Project Management Expertise', desc: 'Decades of construction management experience delivering projects on time and on budget.' },
    { title: 'Transparent Pricing', desc: 'Detailed quotes with no hidden costs. Effective cost management by professionals.' },
  ];
  return (
    <section style={{ background: GH.colors.bg, padding: mob ? '80px 20px' : '120px 40px' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: mob ? 40 : 80, alignItems: 'center' }}>
        <div>
          <Reveal>
            <SectionLabel>Why GreyHolm</SectionLabel>
            <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 3.8vw, 50px)', color: GH.colors.white, letterSpacing: '-0.03em', lineHeight: 1.06, margin: '20px 0 0' }}>
              Experience You Can<br />Count On
            </h2>
          </Reveal>
          <Reveal delay={120} style={{ marginTop: 20 }}>
            <p style={{ fontFamily: GH.fonts.body, fontSize: 16, color: GH.colors.mutedDk, lineHeight: 1.75 }}>
              GreyHolm Group is built by professionals with decades of combined experience across Auckland's construction sector. Every team member brings deep expertise in project management, cost control, and quality delivery.
            </p>
          </Reveal>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: GH.colors.borderDk }}>
          {items.map((item, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={{ background: GH.colors.card, padding: mob ? '24px 20px' : '28px 32px', borderLeft: `3px solid ${i === 0 ? GH.colors.gold : 'transparent'}` }}>
                <div style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 16, color: GH.colors.white, letterSpacing: '-0.01em', marginBottom: 8 }}>{item.title}</div>
                <div style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk, lineHeight: 1.65 }}>{item.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PROCESS ─────────────────────────────────────────────────────────────────

function ProcessSection() {
  const w = useWindowWidth();
  const mob = w < 768;
  const cols = mob ? '1fr' : w < 1024 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';
  const steps = [
    { n: '01', title: 'Get a Quote', desc: 'Submit your project details and we\'ll respond soon with a competitive quote.' },
    { n: '02', title: 'Site Assessment', desc: 'Our team visits to assess scope, timeline, and requirements.' },
    { n: '03', title: 'Detailed Proposal', desc: 'Receive a clear, itemised quote with no hidden costs.' },
    { n: '04', title: 'Works Complete', desc: 'We execute to the highest standard and sign off with you.' },
  ];
  return (
    <section style={{ background: GH.colors.surface, padding: mob ? '80px 20px' : '120px 40px' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', marginBottom: mob ? 48 : 72 }}>
          <SectionLabel>Our Process</SectionLabel>
          <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 3.8vw, 48px)', color: GH.colors.white, letterSpacing: '-0.03em', lineHeight: 1.05, margin: '20px 0 0' }}>
            Simple. Transparent. Efficient.
          </h2>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 2, background: GH.colors.borderDk }}>
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={{ background: GH.colors.card, padding: mob ? '32px 24px' : '40px 32px', position: 'relative' }}>
                <div style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 48, color: 'rgba(28,31,35,0.05)', letterSpacing: '-0.04em', lineHeight: 1, position: 'absolute', top: 24, right: 24 }}>{s.n}</div>
                <div style={{ width: 36, height: 36, background: i === 0 ? GH.colors.gold : GH.colors.charcoal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 13, color: '#fff', marginBottom: 24 }}>{s.n}</div>
                <h3 style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 18, color: GH.colors.white, letterSpacing: '-0.01em', lineHeight: 1.2, margin: '0 0 12px' }}>{s.title}</h3>
                <p style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk, lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BEFORE / AFTER ──────────────────────────────────────────────────────────

function BeforeAfterSection() {
  const w = useWindowWidth();
  const mob = w < 768;
  const [pos, setPos] = useState(50);
  const [drag, setDrag] = useState(false);
  const boxRef = useRef(null);
  const move = useCallback((clientX) => {
    if (!boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    setPos(Math.max(5, Math.min(95, ((clientX - r.left) / r.width) * 100)));
  }, []);
  useEffect(() => {
    if (!drag) return;
    const mm = e => move(e.clientX);
    const tm = e => move(e.touches[0].clientX);
    const up = () => setDrag(false);
    window.addEventListener('mousemove', mm); window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', tm, { passive: false }); window.addEventListener('touchend', up);
    return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('mouseup', up); window.removeEventListener('touchmove', tm); window.removeEventListener('touchend', up); };
  }, [drag, move]);
  const beforeUrl = '/images/before-window.png';
  const afterUrl = '/images/after-window.png';
  const imgH = mob ? 300 : 500;
  return (
    <section style={{ background: GH.colors.surface, padding: mob ? '80px 20px' : '120px 40px' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', marginBottom: mob ? 36 : 60 }}>
          <SectionLabel>Before &amp; After</SectionLabel>
          <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 3.8vw, 48px)', color: GH.colors.white, letterSpacing: '-0.03em', lineHeight: 1.05, margin: '20px 0 12px' }}>
            See the Difference
          </h2>
          <p style={{ fontFamily: GH.fonts.body, fontSize: 15, color: GH.colors.mutedDk }}>Drag the slider to compare before and after our window cleaning service</p>
        </Reveal>
        <Reveal>
          <div ref={boxRef} onMouseDown={e => { setDrag(true); move(e.clientX); }} onTouchStart={e => { setDrag(true); move(e.touches[0].clientX); }}
            style={{ position: 'relative', overflow: 'hidden', cursor: 'col-resize', userSelect: 'none', borderRadius: 2, touchAction: 'none' }}>
            <img src={beforeUrl} alt="Before" style={{ width: '100%', height: imgH, objectFit: 'cover', display: 'block' }} onError={e => { e.currentTarget.style.display='none'; }} />
            <div style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)`, zIndex: 2 }}>
              <img src={afterUrl} alt="After" style={{ width: '100%', height: imgH, objectFit: 'cover', display: 'block' }} onError={e => { e.currentTarget.style.display='none'; }} />
            </div>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: 2, background: '#fff', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 3 }}>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 44, height: 44, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', fontSize: 18, color: GH.colors.charcoal, fontWeight: 700 }}>⟷</div>
            </div>
            <div style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '5px 14px', fontSize: 11, fontFamily: GH.fonts.body, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', borderRadius: 1, zIndex: 3 }}>Before</div>
            <div style={{ position: 'absolute', top: 16, right: 16, background: GH.colors.gold, color: '#fff', padding: '5px 14px', fontSize: 11, fontFamily: GH.fonts.body, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', borderRadius: 1, zIndex: 3 }}>After</div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── CTA BANNER ──────────────────────────────────────────────────────────────

function CTABannerSection({ navigate }) {
  const w = useWindowWidth();
  const mob = w < 768;
  return (
    <section style={{ background: GH.colors.charcoal, padding: mob ? '72px 20px' : '100px 40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(74,124,111,0.06) 0%, transparent 70%)' }} />
      <div style={{ maxWidth: 1300, margin: '0 auto', position: 'relative', zIndex: 1, display: 'flex', flexDirection: mob ? 'column' : 'row', justifyContent: 'space-between', alignItems: mob ? 'flex-start' : 'center', flexWrap: 'wrap', gap: mob ? 32 : 40 }}>
        <Reveal>
          <div style={{ fontFamily: GH.fonts.body, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: GH.colors.gold, marginBottom: 20 }}>Ready to get started?</div>
          <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 4vw, 56px)', color: GH.colors.onDark.heading, letterSpacing: '-0.035em', lineHeight: 1.05, margin: 0 }}>
            Let's build something<br />exceptional together.
          </h2>
        </Reveal>
        <Reveal delay={120} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <GHButton size={mob ? 'md' : 'lg'} onClick={() => navigate('quote')}>Get a Free Quote</GHButton>
          <GHButton size={mob ? 'md' : 'lg'} variant="outline" onClick={() => navigate('contact')}>Contact Us</GHButton>
        </Reveal>
      </div>
    </section>
  );
}

// ─── HOME PAGE ───────────────────────────────────────────────────────────────

export default function HomePage({ navigate }) {
  return (
    <div>
      <HeroSection navigate={navigate} />
      <ServicesOverviewSection navigate={navigate} />
      <WhyChooseSection />
      <ProcessSection />
      <BeforeAfterSection />
      <CTABannerSection navigate={navigate} />
    </div>
  );
}

